// This file contains all the static data for the portfolio.
// The "export" keyword makes these variables available to other files.

// ─── Hero config ─────────────────────────────────────────────────────────────
export const heroConfig = {
    /** Set to false when not actively looking for work */
    availableForProjects: true,
    role: "Software Developer & AI Engineer",
};

export const projectData = {
    'tele-consultation': {
        id: 'tele-consultation',
        imageUrl: '/project-images/app.jpg', // Make sure you have an image for this
        category: 'featured',
        githubUrl: 'https://github.com/Vibaen',
        liveUrl: null,
        title: 'Tele-Consultation App',
        shortDescription: 'A full-stack MERN + React Native mobile app for secure doctor-patient consultations with Zoom integration.',
        subtitle: 'Secure healthcare consultations with integrated video calling',
        techStack: ['React Native', 'Node.js', 'Express.js', 'MongoDB', 'AWS Lambda', 'Stripe API', 'Zoom API'],
        // We are adding the new "caseStudy" object below
        caseStudy: {
            tagline: 'Building a secure and scalable tele-consultation system to connect doctors and patients, improving healthcare accessibility and efficiency.',
            overview: 'The project involved the end-to-end creation of a mobile application that enables patients to find doctors, book appointments, and conduct secure video consultations. As the sole developer, I took complete ownership of the project, from initial design and architecture to full-stack development, testing, and cloud deployment.',
            problem: 'Traditional healthcare systems often face challenges with long wait times, geographical barriers, and inefficient appointment management. Patients in remote areas struggle with access to specialists, while busy professionals find it difficult to schedule in-person visits. The goal was to build a digital platform that would bridge this gap, making healthcare more accessible and convenient for everyone.',
            solution: {
                intro: 'I designed and developed a comprehensive mobile application for both patients and doctors using the MERN stack and React Native. The backend was deployed on AWS Lambda to ensure scalability and cost-efficiency.',
                features: [
                    {
                        title: 'Multi-Role Secure Authentication',
                        text: 'Implemented a robust registration and login system for patients, doctors, and administrators using OTP verification for enhanced security. Created role-based dashboards to provide a tailored user experience for each user type.'
                    },
                    {
                        title: 'Dynamic Appointment Booking System',
                        text: 'Developed a user-friendly interface for patients to browse doctor profiles and book available appointment slots. The system allows doctors to manage their availability and handles real-time scheduling to prevent double bookings.'
                    },
                    {
                        title: 'Integrated Secure Video Consultations',
                        text: 'To facilitate live consultations, I integrated the Zoom API, enabling secure and reliable video calls directly within the app. This choice ensured high-quality video and audio, leveraging a trusted platform.'
                    },
                    {
                        title: 'Scalable, Serverless Backend Architecture',
                        text: 'The backend was built with Node.js and Express.js and deployed on AWS Lambda for automatic scaling based on traffic. This serverless approach minimizes infrastructure management and reduces operational costs.'
                    },
                    {
                        title: 'Online Payments & Digital History',
                        text: 'Integrated the Stripe API to handle secure and seamless payments for consultations. The platform stores consultation history and digital prescriptions, allowing both patients and doctors to access past records easily.'
                    }
                ]
            },
            challenges: [
                {
                    challenge: 'Ensuring real-time synchronization between a doctor\'s availability and a patient\'s booking requests to avoid conflicts.',
                    solution: 'I implemented atomic operations in MongoDB and efficient state management in React Native to ensure the UI always reflected the most current schedule data.'
                },
                {
                    challenge: 'Guaranteeing the security and privacy of sensitive patient data.',
                    solution: 'I designed a strict role-based access control (RBAC) system on the backend. All sensitive data was encrypted at rest and in transit, and API endpoints were secured to prevent unauthorized access.'
                },
                {
                    challenge: 'Optimizing the performance of a serverless architecture.',
                    solution: 'I learned to manage "cold starts" in AWS Lambda by implementing provisioned concurrency for critical functions and optimized database queries to ensure fast API response times.'
                }
            ],
            outcomes: 'The result is a fully functional, production-ready tele-consultation platform that successfully addresses the initial problem statement. The application provides a seamless and secure experience for users, from booking and payment to the final video consultation. The use of a serverless architecture on AWS ensures it can handle a growing user base without manual intervention.'
        }
    },
    'document-translator': {
        id: 'document-translator',
        imageUrl: '/project-images/doc-translator.jpg',
        category: 'featured',
        githubUrl: 'https://github.com/Vibaen',
        liveUrl: null,
        title: 'Document Translator Pipeline',
        shortDescription: 'An OCR + NLP pipeline using Python and Tesseract to automate multilingual document translation.',
        subtitle: 'Automated multilingual document translation using OCR and NLP',
        techStack: ['Python', 'Tesseract OCR', 'OpenCV', 'Google Translate API', 'Flask'],
        goal: 'Build an automated pipeline to extract text from documents and translate them into multiple languages with high accuracy.',
        solution: 'Implemented an OCR pipeline using Tesseract and OpenCV for text extraction, combined with Google Translate API for multilingual translation. Created a Flask web interface for easy document upload and processing.',
        features: [
            '90%+ OCR accuracy with image preprocessing',
            'Support for 50+ languages',
            'Batch processing capabilities',
            'PDF and image format support',
            'Quality assessment and confidence scoring',
            'RESTful API for integration'
        ]
    },
    'ecom-exchanger': {
        id: 'ecom-exchanger',
        imageUrl: '/project-images/e-com.jpg',
        category: 'featured',
        githubUrl: 'https://github.com/Vibaen',
        liveUrl: null,
        title: 'E-com Box Exchanger',
        shortDescription: 'A Django-based platform promoting sustainability by reusing e-commerce boxes, featuring an OpenCV image analysis pipeline.',
        subtitle: 'Sustainable packaging reuse platform with AI-powered quality assessment',
        techStack: ['Django', 'OpenCV', 'PostgreSQL', 'Redis', 'AWS S3'],
        goal: 'Create a platform that promotes sustainability by enabling users to exchange and reuse e-commerce packaging boxes.',
        solution: 'Built a Django-based web platform with OpenCV integration for automated box quality assessment. Implemented a matching algorithm to connect users based on location and box requirements.',
        features: [
            'AI-powered box condition assessment',
            'Location-based matching algorithm',
            'User rating and review system',
            'Real-time chat for coordination',
            'Environmental impact tracking',
            'Mobile-responsive design'
        ]
    },
    'skillsunited': {
        id: 'skillsunited',
        imageUrl: '/project-images/sku-logo.png',
        category: 'featured',
        githubUrl: 'https://github.com/Vibaen',
        liveUrl: null,
        title: 'SkillsUnited FC Website',
        shortDescription: 'A membership and subscription platform built with PHP, MySQL, and Stripe, deployed on AWS EC2.',
        subtitle: 'Membership platform with subscription management and payment processing',
        techStack: ['PHP', 'MySQL', 'Stripe API', 'AWS EC2', 'Bootstrap'],
        goal: 'Develop a comprehensive membership platform for a football club with subscription management and payment processing.',
        solution: 'Created a PHP-based website with a MySQL database, integrated Stripe for payment processing, and deployed on AWS EC2 with auto-scaling capabilities.',
        features: [
            'Recurring payments via Stripe integration',
            'Member dashboard and profile management',
            'Event scheduling and registration',
            'Team statistics and player profiles',
            'Email notifications and reminders',
            'Admin panel for club management'
        ]
    },
    // Add these three new project objects inside your portfolioData.js file

    'wix-sports-store': {
        id: 'wix-sports-store',
        imageUrl: '/project-images/wix-store.png',
        category: 'featured',
        githubUrl: null,
        liveUrl: null,
        title: 'Wix Sports Accessories Store',
        shortDescription: 'An e-commerce website built on the Wix platform for selling sports apparel and accessories.',
        subtitle: 'User-friendly e-commerce store for sports gear enthusiasts',
        techStack: ['Wix Platform', 'E-commerce Management', 'Payment Gateway Integration', 'Product SEO', 'UI/UX Design'],
        goal: 'To create a visually appealing and fully functional online store for sports accessories, enabling easy browsing and secure checkout for customers.',
        solution: 'Utilized the Wix e-commerce platform to design and deploy a mobile-responsive storefront. Integrated payment solutions and configured product catalogs, focusing on a clean user experience and SEO for product visibility.',
        features: [
            'Full product catalog for jerseys, shorts, etc.',
            'Secure online payment processing',
            'Mobile-responsive design for shopping on any device',
            'User account and order history functionality',
            'Integrated contact and support forms',
            'Basic SEO for product pages'
        ]
    },
    'text-to-video-ai': {
        id: 'text-to-video-ai',
        imageUrl: '/project-images/text-to-video-ai.png',
        category: 'featured',
        githubUrl: 'https://github.com/Vibaen',
        liveUrl: null,
        title: 'Text to Video AI Generator',
        shortDescription: 'A Python-based CLI tool that transforms text prompts into engaging videos with captions and audio.',
        subtitle: 'Automated video creation using AI script generation, TTS, and stock footage',
        techStack: ['Python', 'OpenAI API', 'Groq API', 'Pexels API', 'FFmpeg', 'MoviePy', 'ImageMagick'],
        goal: 'To build an automated pipeline that can generate a complete video—including a script, voiceover, captions, and background footage—from a single text prompt.',
        solution: 'Developed a Python application that orchestrates multiple APIs and tools. It uses OpenAI/Groq for script generation, a TTS engine for voiceover, Pexels API for background videos, and leverages MoviePy with FFmpeg for video composition and caption rendering.',
        features: [
            'AI-powered script generation from a prompt',
            'Fetches relevant background videos from Pexels',
            'Generates automatically timed captions',
            'Customizable TTS voice options',
            'Simple Command-Line Interface (CLI) for operation',
            'Modular design for easy updates to models or APIs'
        ]
    },
    'text-to-video-colab': {
        id: 'text-to-video-colab',
        imageUrl: '/project-images/text-to-video-colab.png',
        category: 'featured',
        githubUrl: 'https://github.com/Vibaen',
        liveUrl: null,
        title: 'Text-to-Video on Google Colab',
        shortDescription: 'A Google Colab notebook for generating videos from text prompts, leveraging cloud GPU resources.',
        subtitle: 'Accessible AI video generation using Gemini API within a Colab environment',
        techStack: ['Google Colab', 'Python', 'Gemini API', 'GPU Acceleration', 'Jupyter Notebook'],
        goal: 'To create an accessible, easy-to-run tool for text-to-video generation that leverages the power of cloud GPUs without requiring local setup of complex dependencies.',
        solution: 'Developed a Jupyter Notebook optimized for Google Colab. The notebook uses the Gemini API for content generation and is configured to utilize Colab\'s free GPU hardware acceleration, with secrets management for API keys.',
        features: [
            'Runs entirely in the browser via Google Colab',
            'Leverages Gemini API for video generation logic',
            'Optimized for GPU hardware acceleration',
            'Secure API key handling using Colab Secrets',
            'Step-by-step instructions for easy execution',
            'No local installation of dependencies required'
        ]
    },
    'generative-ai': {
        id: 'generative-ai',
        imageUrl: '/project-images/GCP.jpg',
        category: 'ai',
        githubUrl: 'https://github.com/Vibaen',
        liveUrl: null,
        title: 'Generative AI Internship',
        shortDescription: 'Hands-on experience building and deploying generative models for text and images using Vertex AI.',
        subtitle: 'Google Cloud Vertex AI experience with text and image generation',
        techStack: ['Python', 'Vertex AI', 'TensorFlow', 'Google Cloud Platform', 'Jupyter'],
        goal: 'Gain hands-on experience with generative AI models and deploy them using Google Cloud infrastructure.',
        solution: 'Worked with various generative models including text generation and image synthesis, utilizing Vertex AI for model training and deployment.',
        features: [
            'Text generation with fine-tuned models',
            'Image synthesis and style transfer',
            'Model deployment on Vertex AI',
            'Performance optimization and scaling',
            'API development for model access',
            'Monitoring and logging implementation'
        ]
    },
    'resume-extractor': {
        id: 'resume-extractor',
        imageUrl: '/project-images/resume-data-extractor.png',
        category: 'ai',
        githubUrl: 'https://github.com/Vibaen',
        liveUrl: null,
        title: 'Resume Data Extractor',
        shortDescription: 'An NLP project focused on automated information extraction from resumes.',
        subtitle: 'NLP-powered automated information extraction from resumes',
        techStack: ['Python', 'spaCy', 'NLTK', 'PyPDF2', 'scikit-learn'],
        goal: 'Automate the extraction of key information from resumes to streamline the recruitment process.',
        solution: 'Developed an NLP pipeline using spaCy and NLTK to extract entities like skills, experience, education, and contact information from resume documents.',
        features: [
            'Multi-format support (PDF, DOC, TXT)',
            'Named entity recognition for key information',
            'Skills extraction and categorization',
            'Experience timeline construction',
            'Education qualification parsing',
            'Structured JSON output format'
        ]
    },
    'election-analysis': {
        id: 'election-analysis',
        imageUrl: '/project-images/lok-sabha.png',
        category: 'ai',
        githubUrl: 'https://github.com/Vibaen',
        liveUrl: null,
        title: 'Lok Sabha 2024 Analysis',
        shortDescription: 'A data analytics project providing insights into the 2024 election results.',
        subtitle: 'Comprehensive data analytics of Indian general election results',
        techStack: ['Python', 'Pandas', 'Matplotlib', 'Seaborn', 'Jupyter', 'Plotly'],
        goal: 'Analyze the 2024 Lok Sabha election results to provide insights into voting patterns and demographic trends.',
        solution: 'Collected and processed election data from multiple sources, performed statistical analysis, and created interactive visualizations to reveal key insights.',
        features: [
            'State-wise voting pattern analysis',
            'Demographic correlation studies',
            'Interactive data visualizations',
            'Trend comparison with previous elections',
            'Constituency-level detailed analysis',
            'Predictive modeling for future trends'
        ]
    },
    'beta-bots': {
        id: 'beta-bots',
        imageUrl: '/project-images/beta-bots.jpg', // <-- CHANGE THIS
        category: 'leadership',
        githubUrl: null,
        liveUrl: null,
        title: 'Joint Secretary @ Beta Bots',
        shortDescription: 'Led a 20+ member team, organized hackathons, and mentored juniors in a premier tech club.',
        subtitle: 'Leadership role in premier technology club',
        techStack: ['Leadership', 'Event Management', 'Mentoring', 'Team Building'],
        goal: 'Lead technical initiatives and foster innovation within the college tech community.',
        solution: 'Organized multiple hackathons, technical workshops, and mentoring sessions while managing a diverse team of 20+ members.',
        features: [
            'Organized 5+ major hackathons',
            'Mentored 50+ junior students',
            'Coordinated technical workshops',
            'Managed club operations and events',
            'Built partnerships with industry',
            'Increased club membership by 40%'
        ]
    },
    'urban-uplift': {
        id: 'urban-uplift',
        imageUrl: '/project-images/urban-uplift.jpg',
        category: 'leadership',
        githubUrl: null,
        liveUrl: null,
        title: 'Secretary @ Urban Uplift',
        shortDescription: 'Directed community-driven initiatives and coordinated 25+ volunteers, impacting over 500 individuals.',
        subtitle: 'Community service and social impact initiatives',
        techStack: ['Project Management', 'Community Outreach', 'Volunteer Coordination'],
        goal: 'Drive community-focused initiatives to create positive social impact in urban areas.',
        solution: 'Coordinated multiple community service projects, managed volunteer teams, and established partnerships with local organizations.',
        features: [
            'Coordinated 25+ active volunteers',
            'Impacted over 500 individuals',
            'Organized educational workshops',
            'Led environmental cleanup drives',
            'Established community partnerships',
            'Managed project budgets and resources'
        ]
    },
    'hackathon-finalist': {
        id: 'hackathon-finalist',
        imageUrl: '/project-images/google-hackathon.jpg',
        category: 'leadership',
        githubUrl: 'https://github.com/Vibaen',
        liveUrl: null,
        title: 'Hackathon Finalist',
        shortDescription: 'Developed an AI-powered teacher assistant for multi-grade classrooms, reaching the finals of a 30-hour hackathon.',
        subtitle: 'AI-powered teacher assistant for multi-grade classrooms',
        techStack: ['Python', 'Machine Learning', 'Natural Language Processing', 'Flask'],
        goal: 'Develop an AI assistant to help teachers manage multi-grade classrooms more effectively.',
        solution: 'Created an intelligent system that can adapt teaching content based on student grade levels and learning patterns, reaching the finals in a competitive 30-hour hackathon.',
        features: [
            'Grade-adaptive content delivery',
            'Student progress tracking',
            'Automated assignment generation',
            'Learning pattern analysis',
            'Interactive dashboard for teachers',
            'Real-time performance insights'
        ]
    },
    'class-representative': {
        id: 'class-representative',
        imageUrl: '/project-images/srm-cr.jpg',
        category: 'leadership',
        githubUrl: null,
        liveUrl: null,
        title: 'Class Representative',
        shortDescription: 'Student advocate and liaison between faculty and peers, coordinating academic activities and student welfare.',
        subtitle: 'Student advocate and academic liaison',
        techStack: ['Leadership', 'Communication', 'Conflict Resolution', 'Academic Coordination'],
        goal: 'Serve as the primary bridge between students and faculty to enhance the academic experience and address student concerns.',
        solution: 'Facilitated effective communication between classmates and professors, organized study groups, coordinated academic events, and advocated for student needs and welfare.',
        features: [
            'Represented 60+ classmates in academic matters',
            'Organized peer study sessions and group projects',
            'Coordinated with faculty for curriculum feedback',
            'Mediated student-faculty communication',
            'Organized class events and team-building activities',
            'Maintained academic records and announcements'
        ]
    },
    'symposium-treasurer': {
        id: 'symposium-treasurer',
        imageUrl: '/project-images/aurganon.jpg',
        category: 'leadership',
        githubUrl: null,
        liveUrl: null,
        title: 'Treasurer @ National Symposium',
        shortDescription: 'Led sponsorship team and managed finances for a national-level symposium at SRM, securing funds from multiple entities.',
        subtitle: 'Financial management and sponsorship acquisition for national-level academic event',
        techStack: ['Financial Management', 'Sponsorship Acquisition', 'Team Leadership', 'Budget Planning'],
        goal: 'Secure adequate funding and manage finances for a successful national-level symposium at SRM University.',
        solution: 'Led a dedicated sponsorship team to approach various corporate entities, educational institutions, and local businesses. Developed comprehensive sponsorship packages and managed event budgets to ensure smooth execution.',
        features: [
            'Secured sponsorships from 10+ corporate entities',
            'Managed event budget of ₹5+ lakhs',
            'Led a team of 8 sponsorship coordinators',
            'Developed tiered sponsorship packages',
            'Maintained transparent financial records',
            'Coordinated with vendors and service providers'
        ]
    },
    'placement-rep': {
        id: 'placement-rep',
        imageUrl: '/project-images/srm-pr.jpg',
        category: 'leadership',
        githubUrl: null,
        liveUrl: null,
        title: 'Placement Representative',
        shortDescription: 'Primary liaison for 200+ students, streamlining recruitment and improving interview readiness.',
        subtitle: 'Student liaison for recruitment and career development',
        techStack: ['Communication', 'Data Management', 'Student Counseling', 'Process Optimization'],
        goal: 'Serve as the primary liaison between students and recruitment teams to improve placement outcomes.',
        solution: 'Streamlined the recruitment process, provided career guidance to students, and maintained comprehensive databases of student profiles and company requirements.',
        features: [
            'Managed 200+ student profiles',
            'Coordinated with 15+ companies',
            'Conducted mock interview sessions',
            'Improved placement rate by 25%',
            'Organized career development workshops',
            'Maintained recruitment databases'
        ]
    }
};
// --- SKILLS DATA ---
export const skillsData = [
    {
        category: "Frontend Development",
        skills: ["React", "React Native", "JavaScript (ES6+)", "HTML5", "CSS3", "Redux", "Vue.js", "Tailwind CSS", "Figma", "Canva"]
    },
    {
        category: "Backend Development",
        skills: ["Node.js", "Express.js", "Python", "PHP", "RESTful APIs", "GraphQL", "Supabase"]
    },
    {
        category: "Databases",
        skills: ["MongoDB", "MySQL", "PostgreSQL", "Redis"]
    },
    {
        category: "AI & Data Science",
        skills: ["Machine Learning", "NLP", "Vertex AI", "Pandas", "spaCy", "Scikit-learn", "NumPy"]
    },
    {
        category: "Cloud & DevOps",
        skills: ["AWS (EC2, S3, Lambda)", "Google Cloud Platform", "Docker", "Git & GitHub", "CI/CD"]
    }
];

// --- EXPERIENCE DATA ---
export const experienceData = [
    {
        role: "Software Development Engineer",
        company: "KATTRAPIN TECHNOLOGIES",
        duration: "2024 – Present",
        type: "Engineering",
        tech: ["React", "Node.js", "AWS Lambda", "Stripe", "Zoom API"],
        highlight: "Deployed 5+ production apps serving real clients across web and mobile",
        description: [
            "Developed and deployed full-stack web and mobile applications for various clients.",
            "Architected serverless backends on AWS Lambda to ensure scalability and reduce costs.",
            "Integrated third-party APIs such as Stripe for payments and Zoom for video consultations.",
        ]
    },
    {
        role: "Generative AI Intern",
        company: "Google Cloud",
        duration: "Summer 2024",
        type: "AI & Innovation",
        tech: ["Vertex AI", "PaLM 2", "Python", "GCP"],
        highlight: "Fine-tuned and deployed production-grade LLMs on Google Vertex AI",
        description: [
            "Gained hands-on experience with text and image generation models on Vertex AI.",
            "Fine-tuned foundation models and deployed them as scalable API endpoints.",
            "Developed monitoring and logging solutions for production AI models.",
        ]
    },
    {
        role: "Joint Secretary",
        company: "Beta Bots — Tech Club",
        duration: "2022 – 2025",
        type: "Leadership",
        tech: ["Hackathon Design", "Public Speaking", "Event Management"],
        highlight: "Co-organized 2 national-level symposiums with 300+ participants each",
        description: [
            "Organized hackathons and mentored junior students in robotics and web development.",
            "Co-organized SRM Symposium 2023 and 2024, managing end-to-end event logistics.",
            "Delivered periodic presentations and professional progress reports to faculty boards.",
        ]
    },
];

// --- CERTIFICATES DATA ---
export const certificatesData = [
    {
        name: "Generative AI",
        issuer: "Google Cloud Platform",
        date: "2024",
        link: "https://coursera.org/verify/3X9HK3DYX9M7" // Add link to your certificate
    },
    {
        name: "Python For Data Science",
        issuer: "IBM",
        date: "2024",
        link: "https://courses.cognitiveclass.ai/certificates/3fd361bf7e0049e79d73605bf1164e46" // Add link to your certificate
    },
    {
        name: "Networking and Cloud Computing",
        issuer: "Microsoft",
        date: "2024",
        link: "https://coursera.org/verify/UMAGLK55T7FW" // Add link to your certificate
    }

];

// You can add more data for Blogs, Music, Hobbies as needed
export const aboutMorpheus = `
B.Tech in Information Technology from SRM University with an outstanding 9.49 CGPA.
Morpheus Vibaen J represents the perfect blend of technical excellence and community leadership.
His journey spans from building scalable full-stack applications to leading teams that create meaningful impact in both technology and society.
What sets Morpheus apart isn't just his technical prowess in Software Development, AI/ML Implementations, or Cloud Deployments—it's his ability to bridge the gap between code and community. As Joint Secretary of Beta Bots, he didn't just organize hackathons; he mentored over 50 junior students and grew the club membership by 40%, fostering the next generation of tech innovators.
His role as Secretary at Urban Uplift showcases another dimension—coordinating 25+ volunteers to impact over 500 individuals through community-driven initiatives. This experience taught him that the best solutions aren't just technically sound; they're human-centered and socially conscious.
Key Achievements: 9.49 CGPA, 500+ Lives Impacted, 50+ Students Mentored, 200+ Placement Candidates Guided.
Technical Skills: React Native, Node.js, Python, AWS, MongoDB, Machine Learning.
Leadership Skills: Team Management, Strategic Planning, Mentoring, Community Building, Event Organization, Social Impact.
`;
